import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Button, Card, CircularProgress, Collapse, Popover, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import TaskViewService from '../../services/TaskViewService';
import PostingPanel from './PostingPanel';
import TaskCalendar, { TaskCard } from './TaskCalendar';
import {
    addDays,
    dayCards,
    dayLabel,
    monthEnd,
    monthGrid,
    monthKey,
    monthName,
    monthStart,
    notPostedCount,
    postingKey,
    rowsByDay,
} from './calendarUi';
import { categoryLabel, platformLabel, useTaskPlatforms } from './taskFields';
import useTaskSummary from './useTaskSummary';

const VIEW = 'posting-calendar';
// The list asks for about a month back and two months on; the server allows 93 days at once
const DAYS_BACK = 32;
const DAYS_ON = 60;

const browserToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** One day's heading and cards on the list. */
const Day = ({ iso, rows, today, onOpen, onOpenGroup }) => (
    <Box component="section" aria-label={dayLabel(iso, true)} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            {dayLabel(iso, true)}
            {iso === today ? ' · today' : ''}
        </Typography>
        {dayCards(rows, true, postingKey).map((card) => (
            <TaskCard key={card.key} card={card} onOpen={onOpen} onOpenGroup={onOpenGroup} posting today={today} />
        ))}
    </Box>
);

/** The list: Earlier folded, then today on by day, then No date as one group. */
const PostingList = ({ data, onOpen, onShowMonth }) => {
    const [earlierOpen, setEarlierOpen] = useState(false);
    const [languages, setLanguages] = useState(null);
    const today = data.today;
    const byDay = rowsByDay(data.rows);
    const days = Object.keys(byDay).sort();
    const earlier = days.filter((d) => d < today);
    const coming = days.filter((d) => d >= today);
    const notPosted = earlier.reduce((n, d) => n + notPostedCount(dayCards(byDay[d], true, postingKey)), 0);
    const earlierCards = earlier.reduce((n, d) => n + dayCards(byDay[d], true, postingKey).length, 0);
    const noDate = data.noDate?.rows || [];
    const openGroup = (anchor, card) => setLanguages({ anchor, card });
    return (
        <Box>
            {earlier.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        size="small"
                        color="inherit"
                        onClick={() => setEarlierOpen((o) => !o)}
                        aria-expanded={earlierOpen}
                        endIcon={earlierOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                        Earlier ({earlierCards}){notPosted > 0 ? ` · ${notPosted} not posted` : ''}
                    </Button>
                    <Collapse in={earlierOpen} unmountOnExit>
                        <Box sx={{ mt: 1 }}>
                            {[...earlier].reverse().map((iso) => (
                                <Day key={iso} iso={iso} rows={byDay[iso]} today={today} onOpen={onOpen} onOpenGroup={openGroup} />
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )}
            {coming.map((iso) => (
                <Day key={iso} iso={iso} rows={byDay[iso]} today={today} onOpen={onOpen} onOpenGroup={openGroup} />
            ))}
            {coming.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nothing is posting from today on.
                </Typography>
            )}
            {data.nextDate && (
                <Button size="small" sx={{ mb: 2 }} onClick={() => onShowMonth(monthKey(data.nextDate))}>
                    See later ones from {dayLabel(data.nextDate)}
                </Button>
            )}
            {noDate.length > 0 && (
                <Box component="section" aria-label="No date">
                    <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
                        No date ({data.noDate.count})
                    </Typography>
                    {dayCards(noDate, true, postingKey).map((card) => (
                        <TaskCard key={card.key} card={card} onOpen={onOpen} onOpenGroup={openGroup} posting today={today} />
                    ))}
                    {data.noDate.count > noDate.length && (
                        <Typography variant="caption" color="text.secondary" component="div">
                            Showing {noDate.length} of {data.noDate.count}. Narrow the filters to see the rest.
                        </Typography>
                    )}
                </Box>
            )}
            <Popover open={!!languages} anchorEl={languages?.anchor} onClose={() => setLanguages(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                {languages && (
                    <Box sx={{ p: 1.5, width: 280 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {languages.card.rows[0].title}
                        </Typography>
                        {languages.card.rows.map((r) => (
                            <TaskCard
                                key={`${r.id}:${r.slot || 1}`}
                                card={{ key: r.id, rows: [r], grouped: false }}
                                onOpen={(row) => {
                                    setLanguages(null);
                                    onOpen(row);
                                }}
                                posting
                                today={today}
                                compact
                            />
                        ))}
                    </Box>
                )}
            </Popover>
        </Box>
    );
};

/** Done posts with no date. It's how AV finds a post in an emergency. */
const PostBank = ({ bank, categories, onOpen }) => {
    const platforms = useTaskPlatforms();
    const posts = bank?.posts || [];
    if (posts.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="subtitle1">The post bank is empty</Typography>
                <Typography variant="body2" color="text.secondary">
                    Done posts with no posting date wait here.
                </Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Done posts with no posting date. Open one to give it a date.
            </Typography>
            {posts.map((p) => (
                <Card
                    key={p.contentId}
                    variant="outlined"
                    component="button"
                    type="button"
                    onClick={() => onOpen({ id: p.taskId })}
                    sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        font: 'inherit',
                        color: 'inherit',
                        p: 1,
                        mb: 0.5,
                        cursor: 'pointer',
                        backgroundColor: 'background.paper',
                        '&:hover': { borderColor: 'primary.main' },
                        '&:focus-visible': { outline: '3px solid', outlineColor: 'secondary.main', outlineOffset: 1 },
                    }}
                >
                    <Typography variant="caption" component="div" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                        {p.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                        {[
                            platformLabel(platforms, p.platform),
                            categoryLabel(categories, p.categoryKey),
                            `${p.languages} language${p.languages === 1 ? '' : 's'}`,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </Typography>
                </Card>
            ))}
            {bank.count > posts.length && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Showing {posts.length} of {bank.count}. Narrow the filters to see the rest.
                </Typography>
            )}
        </Box>
    );
};

/**
 * Posting calendar (rev6 Part 5): every post and video with a Platform. Opens on the list from
 * today, with Calendar for the month and the Post bank. It reads its own data, because the list and
 * the month want different dates. The page's layout control switches between them (Part 13).
 *
 * tab: list, calendar or bank, from the URL. onBankCount: the bank's count, for that control. refresh: a number that goes up when the page wants a
 * reload. filtered: filters are on, so an empty result offers to clear them. date: the day an email
 * linked to, which picks the month. AV gets the Posting panel in place of the task drawer.
 */
const PostingCalendar = ({ filter, tab, onTab, onOpen, refresh, categories, filtered, onClearAll, onError, onMatchText, onBankCount, date }) => {
    useTrackUsage('PostingList', 'screen', 'posts', tab === 'list');
    useTrackUsage('PostingCalendar', 'screen', 'posts', tab === 'calendar');
    useTrackUsage('PostBank', 'screen', 'posts', tab === 'bank');
    const avOnly = !!useTaskSummary()?.avOnly;
    const [panelRow, setPanelRow] = useState(null);
    const [reloads, setReloads] = useState(0);
    const [month, setMonth] = useState(() => monthKey(date || browserToday()));
    const [data, setData] = useState(null);
    const [bank, setBank] = useState(null);
    const [loading, setLoading] = useState(false);
    const latest = useRef(0);
    const monthSettled = useRef(!!date);

    // Only the month's grid needs the dates; the bank tab keeps the list's for its counts
    const onMonth = tab === 'calendar';
    const filterKey = JSON.stringify(filter);

    useEffect(() => {
        const request = ++latest.current;
        const { page, size, group, sort, direction, ...f } = JSON.parse(filterKey);
        const today = browserToday();
        const weeks = monthGrid(month);
        const dates = onMonth
            ? { from: weeks[0][0], to: weeks[weeks.length - 1][6] }
            : { from: addDays(today, -DAYS_BACK), to: addDays(today, DAYS_ON) };
        setLoading(true);
        Promise.all([TaskViewService.getCalendar(VIEW, f, { on: 'posting', ...dates }), TaskViewService.getPostBank(f)])
            .then(([calendarData, bankData]) => {
                if (request !== latest.current) return;
                setData(calendarData);
                setBank(bankData);
                // The first month follows the server's today (India time), not this browser's
                if (!monthSettled.current) {
                    monthSettled.current = true;
                    if (calendarData.today && monthKey(calendarData.today) !== month) setMonth(monthKey(calendarData.today));
                }
            })
            .catch((e) => {
                if (request === latest.current) onError(taskErrorMessage(e));
            })
            .finally(() => {
                if (request === latest.current) setLoading(false);
            });
    }, [filterKey, onMonth, onMonth && month, refresh, reloads]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!data) {
            onMatchText(null);
        } else if (tab === 'bank') {
            onMatchText(`${bank?.count || 0} post${bank?.count === 1 ? '' : 's'} in the bank`);
        } else if (onMonth) {
            const n = data.rows.filter((r) => r.date >= monthStart(month) && r.date <= monthEnd(month)).length;
            onMatchText(`${n} task${n === 1 ? '' : 's'} in ${monthName(month)}`);
        } else {
            const n = data.rows.length + (data.noDate?.count || 0);
            onMatchText(`${n} task${n === 1 ? '' : 's'} match`);
        }
    }, [data, bank, tab, month, onMonth]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        onBankCount(bank ? bank.count : null);
    }, [bank]); // eslint-disable-line react-hooks/exhaustive-deps

    const openRow = avOnly ? setPanelRow : onOpen;

    const showMonth = (key) => {
        setMonth(key);
        onTab('calendar');
    };

    // The calendar stays on an empty month, or its arrows would go with it
    const nothing = !!data && !onMonth && data.rows.length === 0 && !data.noDate?.count && !data.nextDate && !bank?.count;

    return (
        <Box>
            {!data && loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {data && nothing && filtered && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="subtitle1">No tasks match these filters</Typography>
                    <Button sx={{ mt: 1 }} onClick={onClearAll}>
                        Clear all
                    </Button>
                </Box>
            )}
            {data && nothing && !filtered && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="subtitle1">Nothing to post yet</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Posts, and videos with a Platform, show up here.
                    </Typography>
                </Box>
            )}

            {data && !nothing && (
                <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                    {data.truncated && tab !== 'bank' && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            There are more tasks than this can show. Narrow the filters to see them all.
                        </Alert>
                    )}
                    {data.needReady > 0 && tab !== 'bank' && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {data.needReady} need{data.needReady === 1 ? 's' : ''} Ready to post
                        </Typography>
                    )}
                    {tab === 'list' && <PostingList data={data} onOpen={openRow} onShowMonth={showMonth} />}
                    {tab === 'calendar' && (
                        <TaskCalendar
                            data={data}
                            month={month}
                            onMonthChange={setMonth}
                            group
                            groupKey={postingKey}
                            onOpen={openRow}
                            posting
                        />
                    )}
                    {tab === 'bank' && <PostBank bank={bank} categories={categories} onOpen={openRow} />}
                </Box>
            )}
            <PostingPanel row={panelRow} onClose={() => setPanelRow(null)} onChanged={() => setReloads((n) => n + 1)} />
        </Box>
    );
};

export default PostingCalendar;
